import {useEffect,useRef,useState,type ReactNode} from 'react';
import {Link} from 'react-router';
import {api,resumeResearch,streamChat,type StreamEvent} from '../lib/api';
import {brand} from '../config/brand';
import {scriptureRangeLabel,type ScriptureSelection} from '../shared/bible';
import type {AnswerLength,ResearchRun} from '../shared/research';
import {StudyWorkspace} from './StudyWorkspace';
import {MarkdownContent} from './MarkdownContent';

/** Keyed by edition and passage so an old answer never follows a new selection. */
export function ReaderStudy({selections,userId,onAuth,children}:{selections:ScriptureSelection[];userId:string|null;onAuth:()=>void;children:ReactNode}){
  const receiptKey=`reader-research:${userId}:${JSON.stringify(selections)}`;
  const [run,setRun]=useState<ResearchRun|null>(null);
  const [receipt,setReceipt]=useState<{runId:string;conversationId:string;messageId:string}|null>(null);
  const [busy,setBusy]=useState(false);
  const [progress,setProgress]=useState('');
  const [error,setError]=useState('');
  const [question,setQuestion]=useState('');
  const [length,setLength]=useState<AnswerLength>('standard');
  const [saved,setSaved]=useState(false);
  const controller=useRef<AbortController|null>(null);
  const latestRunId=useRef<string|null>(null);
  const onEvent=(event:StreamEvent)=>{
    if(event.type==='meta'&&event.runId&&event.conversationId&&event.messageId){
      const value={runId:event.runId,conversationId:event.conversationId,messageId:event.messageId};
      latestRunId.current=value.runId;setReceipt(value);sessionStorage.setItem(receiptKey,JSON.stringify(value));
    }
    if(event.progressLabel)setProgress(event.progressLabel);
    if(event.researchRun)setRun(event.researchRun);
  };
  useEffect(()=>{
    const abort=new AbortController();controller.current=abort;
    let previous:typeof receipt=null;
    try{previous=JSON.parse(sessionStorage.getItem(receiptKey)??'null');}catch{/* Ignore obsolete receipts. */}
    if(previous&&userId){
      setReceipt(previous);latestRunId.current=previous.runId;setBusy(true);
      void resumeResearch(previous.runId,onEvent,abort.signal).catch(caught=>{if(!abort.signal.aborted)setError(String(caught.message??caught));}).finally(()=>{if(!abort.signal.aborted)setBusy(false);});
    }
    return()=>abort.abort();
    // This component is remounted for a different selection or account.
  },[receiptKey,userId]);
  async function ask(prompt:string){
    if(!userId){onAuth();return;}
    if(busy||!selections.length)return;
    const abort=new AbortController();controller.current?.abort();controller.current=abort;
    setBusy(true);setError('');setRun(null);setSaved(false);setProgress('Reading the passage');
    try{await streamChat({prompt,mode:'scripture',answerLength:length,scriptureSelections:selections,...(receipt?{conversationId:receipt.conversationId}:{})},onEvent,abort.signal);setQuestion('');}
    catch(caught){if(!abort.signal.aborted)setError(caught instanceof Error?caught.message:'Research could not be completed.');}
    finally{if(!abort.signal.aborted)setBusy(false);}
  }
  async function cancel(){
    if(!latestRunId.current)return;
    try{await api(`/api/research/runs/${latestRunId.current}/cancel`,{method:'POST'});controller.current?.abort();setBusy(false);setProgress('');setError('Research canceled.');sessionStorage.removeItem(receiptKey);}
    catch(caught){setError(String(caught));}
  }
  const explanation=`Explain ${selections.flatMap(selection=>selection.ranges.map(scriptureRangeLabel)).join('; ')} in its literary and historical context.`;
  return <StudyWorkspace selections={selections} run={run} onAsk={value=>setQuestion(value)} footer={selections.length?<>
      <form className="reader-study-composer" onSubmit={event=>{event.preventDefault();if(question.trim())void ask(question.trim());}}>
        <label className="answer-length">Answer length<select value={length} onChange={event=>setLength(event.target.value as AnswerLength)} disabled={busy}><option value="brief">Brief</option><option value="standard">Standard</option><option value="detailed">Detailed</option></select></label>
        <div className="row"><textarea className="textarea compact" aria-label="Ask about this passage" value={question} onChange={event=>setQuestion(event.target.value)} placeholder="Ask about this passage…"/><button className="btn primary" aria-label="Send passage question" disabled={busy||!question.trim()}>Ask</button></div>
      </form>
      {run?.answer?          <div className="row"><button className="btn" disabled={saved} onClick={()=>void api('/api/study/notes',{method:'POST',body:JSON.stringify({source:{sourceType:'message',messageId:receipt?.messageId}})}).then(()=>setSaved(true)).catch(caught=>setError(String(caught)))}>{saved?'Saved to studies':'Save study'}</button>
          <Link className="btn" to={`${brand.routes.askAssistant}?conversation=${receipt?.conversationId}`}>Open conversation</Link></div>:null}
    </>:null}>
    <div className="stack reader-grounded-answer">
      {children}
      {selections.length?<>

        {!run&&!busy?<button className="btn primary" onClick={()=>void ask(question.trim()||explanation)}>{error?'Retry explanation':'Explain this passage'}</button>:null}
        {busy?<div role="status" aria-live="polite" className="research-progress"><span>{progress}</span><button className="btn" onClick={()=>void cancel()}>Stop</button></div>:null}
        {error?<p role="alert">{error}</p>:null}
        {run?.answer?<><div className="tiny muted">AI-generated commentary</div><MarkdownContent className="ai-commentary">{run.answer}</MarkdownContent>
</>:null}

      </>:null}
    </div>
  </StudyWorkspace>;
}
